import type { Amount, Currency, TaxId } from '@hero/schema';
import { computeAmountPreview } from './computeAmountPreview';
import { roundPrice } from '../price/round.price';

/**
 * Turn a published credit price into a canonical {@link Amount}.
 *
 * `taxInclusive` is how the catalog number is **interpreted**, not a
 * display hint:
 * - `true`  — buyer pays `pricePerUnit × qty`; VAT is inside. Passes
 *   `total` into {@link computeAmountPreview} so `splitInclusiveTax`
 *   back-derives the subtotal (same path as `AmountService`).
 * - `false` — `pricePerUnit × qty` is pre-tax; VAT is added on top.
 *
 * Never multiply a rate by `1.15` at a call site. Quote cards, the
 * subscription preview, and Billing drafts all go through here so they
 * cannot drift by a halala.
 */
export function amountForUsagePurchase(input: {
  qty: number;
  pricePerUnit: number;
  currency: Currency;
  /** `null` / omit — no tax (e.g. USD). */
  taxId?: TaxId | null;
  taxInclusive?: boolean;
}): Amount {
  const catalogAmount = roundPrice(input.pricePerUnit * Math.max(0, input.qty));
  return amountFromCatalogPrice({
    currency: input.currency,
    catalogAmount,
    taxId: input.taxId,
    taxInclusive: input.taxInclusive,
  });
}

/**
 * Same as {@link amountForUsagePurchase} when the caller already has a
 * catalog total (period subscriptions: seats × months).
 */
export function amountFromCatalogPrice(input: {
  currency: Currency;
  catalogAmount: number;
  taxId?: TaxId | null;
  taxInclusive?: boolean;
}): Amount {
  const taxId = input.taxId ?? undefined;
  if (input.taxInclusive && taxId && input.catalogAmount > 0) {
    return computeAmountPreview({
      currency: input.currency,
      total: input.catalogAmount,
      taxId,
    });
  }
  return computeAmountPreview({
    currency: input.currency,
    itemsOriginalSubtotal: input.catalogAmount,
    ...(taxId ? { taxId } : {}),
  });
}
